import { Suspense } from 'react';
import { SkeletonTheme } from 'react-loading-skeleton';
import { Provider as ReduxProvider, useSelector } from 'react-redux';
import { themeConfig as themeMain } from '@make-software/cspr-design';

import Root from './root';
import { CacheProvider, WalletServiceProvider } from './services';
import './i18n';
import { createStore } from './redux';
import { useReduxStorage } from './storage';
import AppErrorHandler from './app-error-handler';
import { ThemeProvider } from 'styled-components';
import { RootState } from 'typesafe-actions';
import { ClickProvider } from '@make-software/csprclick-ui';
import { CasperNetworkProvider } from './services/casper-network-context.tsx';
import CookieConsentBanner from './components/cookie-consent-banner/cookie-consent-banner';

(window as any)['global'] = window;

declare global {
  interface Window {
    gtag: any;
    reviveAsync: any;
  }
}

let store: ReturnType<typeof createStore>;

const ThemeWrapper = ({ children }) => {
  const { mode } = useSelector((state: RootState) => state.theme);

  return (
    <ThemeProvider theme={themeMain[mode]}>
      <SkeletonTheme
        baseColor={themeMain[mode].styleguideColors.backgroundPrimary}
        highlightColor={themeMain[mode].styleguideColors.fillSecondary}
      >
        {children}
      </SkeletonTheme>
    </ThemeProvider>
  );
};

const clickOptions = {
  appName: 'CSPR.live',
  appId: config.click_app_id,
  apiKey: 'csprclick-api-key',
  csprclickHost: config.cspr_click_host,
  casperNode: config.cspr_rpc_url,
  contentMode: 'iframe',
  iframeContainer: undefined,
  providers: config.cspr_click_wallet_providers,
  walletConnect:
    config.cspr_click_wc_projectid?.length > 0
      ? {
          relayUrl: 'wss://relay.walletconnect.com',
          projectId: config.cspr_click_wc_projectid,
        }
      : undefined,
};

export const App = () => {
  const { reduxStorageState, setReduxStorageState } = useReduxStorage();

  if (store == null) {
    store = createStore(reduxStorageState);
    store.subscribe(() => {
      const { wallet, currencies, theme, featureSpotlights, tablesView } =
        store.getState();
      try {
        setReduxStorageState({
          wallet,
          currencies,
          theme,
          featureSpotlights,
          tablesView,
        });
      } catch {
        // initialization workaround v1
      }
    });
  }

  return (
    <>
      <AppErrorHandler>
        <Suspense fallback={null}>
          <ReduxProvider store={store}>
            <ThemeWrapper>
              <CacheProvider>
                <ClickProvider options={clickOptions}>
                  <CasperNetworkProvider>
                    <WalletServiceProvider>
                      <Root />
                      <CookieConsentBanner />
                    </WalletServiceProvider>
                  </CasperNetworkProvider>
                </ClickProvider>
              </CacheProvider>
            </ThemeWrapper>
          </ReduxProvider>
        </Suspense>
      </AppErrorHandler>
    </>
  );
};

export default App;
